/**
 * Replaces an element with it's updated version.
 *
 * @param {HTMLElement} element - The current element.
 * @param {HTMLElement} updatedElement - The updated element.
 */
export function replaceElement(element, updatedElement) {
  if (!element) return;

  if (!updatedElement) {
    element.remove();
    return;
  }

  if (element.isEqualNode(updatedElement)) {
    return;
  }

  element.replaceWith(updatedElement);
}

/**
 * Capitalizes the string.
 * @param {String} string - The source string to format.
 * @returns {String}
 */
export function capitalize(string) {
  return `${string.charAt(0).toUpperCase()}${string.slice(1)}`;
}

/**
 * Returns elements related to the parent element.
 *
 * @param {HTMLElement} element - The parent custom element.
 * @returns {Record<string, HTMLElement & { all: HTMLElement[] }>}
 * @example <div data-target="example-element.item"></div>
 */
export function getTargets(element) {
  if (!element) {
    return null;
  }

  const tagName = element.tagName.toLowerCase();

  return [...element.querySelectorAll(`[data-target^="${tagName}"]`)].reduce((targets, target) => {
    const key = target.dataset.target.replace(`${tagName}.`, '');

    /**
     * Allows for nesting of the same component.
     */
    if (tagName === element.tagName.toLowerCase() && !(target.closest(tagName) === element)) {
      return targets;
    }

    if (!targets[key]) {
      /**
       * Define the default (first) target.
       */
      targets[key] = target;

      /**
       * Define the `all` property, for multiple targets of the same name.
       */
      targets[key].all = [target];
    } else {
      /**
       * Push subsequent elements to the `all` array.
       */
      targets[key].all = [...targets[key].all, target];
    }

    return targets;
  }, {});
}

/**
 * Returns parsed HTML from a string.
 * @param {String} text - The text to parse.
 * @param {String} selector - Optional child selector.
 * @returns {Document|HTMLElement}
 */
export function parseHTML(text, selector) {
  const parsed = new DOMParser().parseFromString(text, 'text/html');

  if (selector) {
    return parsed.querySelector(selector);
  }

  return parsed;
}

/**
 * Adds an event listener to an element or elements.
 * @param {String} event - The event name.
 * @param {HTMLElement | HTMLElement[]} element - The element(s) to apply the listener to.
 * @param {Function} handler - The listener callback.
 * @param {AddEventListenerOptions} options - Options for the event listener.
 * @param {Boolean} immediate - Runs the handler immedaitely, without an event payload.
 * @returns {Function} Removes the event listeners.
 */
export function on(event, element, handler, options, immediate) {
  const add = (element) => element.addEventListener(event, handler, options);
  const remove = (element) => element.removeEventListener(event, handler, options);

  if (!element) {
    return () => null;
  }

  if (immediate) handler();

  if (Array.isArray(element)) {
    [...element].forEach(add);

    return () => {
      [...element].forEach(remove);

      return null;
    };
  }

  add(element);

  return () => {
    remove(element);

    return null;
  };
}

/**
 * State classes.
 */
export const classes = {
  active: 'is-active',
  open: 'is-open',
  intersecting: 'is-intersecting',
  intersected: 'has-intersected',
  disabled: 'is-disabled',
};

/**
 * Returns if the element has a transition in CSS.
 * @param {HTMLElement} element - The element to test.
 * @returns {Boolean}
 */
export function hasTransition(element) {
  return (
    (parseFloat(getComputedStyle(element).transitionDuration) ||
      parseFloat(getComputedStyle(element).transitionDelay)) > 0
  );
}

/**
 * Disables vertical body scrolling.
 */
export function disableBodyScroll() {
  document.body.style.overflow = 'hidden';
}

/**
 * Enables vertical body scrolling.
 */
export function enableBodyScroll() {
  document.body.style.overflow = '';
}

/**
 * Returns a parsed attribute values.
 * @param {HTMLElement} element - The element to get attributes from.
 * @param {String} attribute - The attribute name.
 * @param {any} fallback - The fallback value, if the attribute isn't found.
 * @returns {Boolean|Number|String}
 */
export function getAttributeValue(element, attribute, fallback = null) {
  const value = element.getAttribute(attribute);

  if (value === null) {
    return fallback;
  }

  if (value === '' || value.toLowerCase() === 'true') {
    return true;
  }

  if (value.toLowerCase() === 'false') {
    return false;
  }

  if (!isNaN(Number(value))) {
    return Number(value);
  }

  return value;
}

/**
 * Returns focusable elements inside a container.
 * @param {HTMLElement} container - The container to find elements.
 * @returns {HTMLElement[]}
 */
export function getFocusableElements(container) {
  return [
    ...container.querySelectorAll(
      [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'details',
        'summary',
        '[tabindex]',
        '[contenteditable="true"]',
      ].join(', ')
    ),
  ];
}

/**
 * Traps focus inside a container.
 * @param {HTMLElement} container - The container to trap focus in.
 * @returns {Function} Releases the focus trap.
 */
export function trapFocus(container) {
  const focusable = getFocusableElements(container);
  const firstFocusable = focusable[0] ?? container;
  const lastFocusable = focusable[focusable.length - 1] ?? container;
  const previouslyFocused = document.activeElement;

  container.focus();

  const removeFocusinEvent = on(
    'focus',
    document,
    (event) => {
      if (!container.contains(event.target)) {
        (firstFocusable || container).focus();
      }
    },
    true
  );

  const removeKeydownEvent = on('keydown', container, (event) => {
    if (event.key === 'Tab' || event.keyCode === 9) {
      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          event.preventDefault();
        }

        return;
      }

      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        event.preventDefault();
      }
    }
  });

  return () => {
    removeFocusinEvent();
    removeKeydownEvent();

    container.blur();

    if (previouslyFocused) {
      previouslyFocused.focus();
    }
  };
}

/**
 * Returns the names of cart-related sections.
 * @returns {String[]}
 */
export function getCartSectionNames() {
  const names = ['header'];
  const cartDrawerElement = document.querySelector('.shopify-section-cart-drawer');

  if (cartDrawerElement) names.push(cartDrawerElement.id.replace('shopify-section-', ''));
  if (theme.page_type === 'cart') names.push('main-cart');

  return names;
}

/**
 * Returns a Promise for waiting for the transition-end.
 * @param {HTMLElement} element - The element to wait for.
 * @returns {Promise}
 */
export function waitForTransitionEnd(element) {
  return new Promise((resolve) => {
    if (!hasTransition(element)) {
      resolve();
    }

    let removeEvent;

    const onTransitionEnd = (event) => {
      event.stopPropagation();

      removeEvent();
      resolve();
    };

    removeEvent = on('transitionend', element, onTransitionEnd);
  });
}

/**
 * Returns a function, that, as long as it continues to be invoked,
 * will not be triggered. The function will be called after it stops
 * being called for N milliseconds. If `immediate` is passed, trigger
 * the function on the leading edge, instead of the trailing.
 *
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null;
  let initialCall = true;

  return function (...args) {
    const callNow = immediate && initialCall;
    function next() {
      callback.apply(this, args);
      timeout = null;
    }

    if (callNow) {
      initialCall = false;
      next();
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait);
    }
  };
}

/**
 * Debounce functions for better performance
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null;

  return function (...args) {
    const later = function () {
      timeout = null;
      if (!immediate) {
        callback.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;
    window.clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);

    if (callNow) {
      callback.apply(this, args);
    }
  };
}

/**
 * Returns if the element is visible in the container.
 * @param {HTMLElement} element - The element to check.
 * @param {HTMLElement} container - The container to use for visibility.
 * @returns {Boolean}
 */
export function isElementVisible(element, container) {
  if (!element || !container) return false;

  const {
    top: elementTop,
    bottom: elementBottom,
    left: elementLeft,
    right: elementRight,
  } = element.getBoundingClientRect();

  const {
    top: containerTop,
    bottom: containerBottom,
    left: containerLeft,
    right: containerRight,
  } = container.getBoundingClientRect();

  return (
    elementTop < containerBottom &&
    elementBottom > containerTop &&
    elementLeft < containerRight &&
    elementRight > containerLeft
  );
}

/**
 * Returns if the element has overflowing content.
 * @param {HTMLElement} element - The element to test.
 * @returns {Boolean}
 */
export function hasOverflow(element) {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

/**
 * Formats a number into money.
 * @param {Number} amount - The amount to format in cents.
 * @returns {String}
 */
export function formatMoney(amount) {
  return new Intl.NumberFormat(`${Shopify.locale}-${Shopify.country}`, {
    style: 'currency',
    currency: Shopify.currency.active,
    minimumFractionDigits: 0,
  }).format(amount / 100);
}

/**
 * Pushes a query parameter to the current location state.
 * @param {String} key - The parameter key.
 * @param {String} value - The value to push (optional).
 */
export function updateQueryParam(key, value, push = false) {
  const url = new URL(window.location.href);

  if (value === null) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }

  window.history[push ? 'pushState' : 'replaceState']({}, '', url);
}
